import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../auth";
import api from "../api";
import { LayoutDashboard, Package, Megaphone, Rocket, LogOut, Settings as SettingsIcon, Users, Shield, CreditCard, BarChart3, UserCircle, Cable, DollarSign, Target } from "lucide-react";

export default function AppLayout() {
  const { user, logout, loading } = useAuth();
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/announcements/active")
      .then((r) => setAnnouncements(r.data.announcements || []))
      .catch(() => setAnnouncements([]));
  }, [user]);
  if (loading) return <div className="p-10 font-mono text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const pct = Math.min(100, Math.round((user.generations_used / user.plan_limit) * 100));

  const link = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 border-l-4 font-mono text-sm uppercase tracking-wider transition-colors ${
      isActive
        ? "border-[#FF3333] bg-zinc-900 text-white"
        : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900"
    }`;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#09090B] text-zinc-100">
      <header className="md:hidden border-b border-zinc-800 bg-[#09090B] sticky top-0 z-20">
        <div className="px-4 py-3 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[#FFD600]" strokeWidth={2.5} />
            <span className="font-heading text-2xl tracking-wide">Income Desk</span>
          </Link>
          <button onClick={logout} className="p-2 text-zinc-400 hover:text-[#FF3333]" title="Log out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <nav className="px-2 pb-2 flex gap-1 overflow-x-auto">
          <NavLink to="/app" end className={link}>Sell first</NavLink>
          <NavLink to="/app/acquire" className={link}>Get clients</NavLink>
          <NavLink to="/app/money" className={link}>Daily money</NavLink>
          <NavLink to="/app/products" className={link}>Products</NavLink>
          <NavLink to="/app/billing" className={link}>Billing</NavLink>
          <NavLink to="/app/platforms" className={link}>Platforms</NavLink>
          <NavLink to="/app/referrals" className={link}>Referrals</NavLink>
          <NavLink to="/app/account" className={link}>Account</NavLink>
          {user.role === "admin" && <NavLink to="/app/admin" className={link}>Admin</NavLink>}
        </nav>
      </header>
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 border-r border-zinc-800 flex-col" data-testid="sidebar">
        <Link
          to="/app"
          className="px-5 py-6 border-b border-zinc-800 flex items-center gap-2"
          data-testid="brand-link"
        >
          <DollarSign className="w-6 h-6 text-[#FFD600]" strokeWidth={2.5} />
          <span className="font-heading text-3xl tracking-wide">Income Desk</span>
        </Link>
        <nav className="flex-1 py-4">
          <NavLink to="/app" end className={link} data-testid="nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Sell First
          </NavLink>
          <NavLink to="/app/acquire" className={link} data-testid="nav-acquire">
            <Target className="w-4 h-4" /> Get Clients
          </NavLink>
          <NavLink to="/app/money" className={link} data-testid="nav-money">
            <DollarSign className="w-4 h-4" /> Daily Money
          </NavLink>
          <NavLink to="/app/products" className={link} data-testid="nav-products">
            <Package className="w-4 h-4" /> Products
          </NavLink>
          <NavLink to="/app/campaigns" className={link} data-testid="nav-campaigns">
            <Megaphone className="w-4 h-4" /> Ad Copy
          </NavLink>
          <NavLink to="/app/launch" className={link} data-testid="nav-launch">
            <Rocket className="w-4 h-4" /> Live Listings
          </NavLink>
          <NavLink to="/app/billing" className={link} data-testid="nav-billing">
            <CreditCard className="w-4 h-4" /> Billing
          </NavLink>
          {user.role === "admin" && (
            <NavLink to="/app/analytics" className={link} data-testid="nav-analytics">
              <BarChart3 className="w-4 h-4" /> Analytics
            </NavLink>
          )}
          <NavLink to="/app/settings" className={link} data-testid="nav-settings">
            <SettingsIcon className="w-4 h-4" /> Store Keys
          </NavLink>
          <NavLink to="/app/platforms" className={link} data-testid="nav-platforms">
            <Cable className="w-4 h-4" /> Channels
          </NavLink>
          <NavLink to="/app/referrals" className={link} data-testid="nav-referrals">
            <Users className="w-4 h-4" /> Referrals
          </NavLink>
          <NavLink to="/app/account" className={link} data-testid="nav-account">
            <UserCircle className="w-4 h-4" /> Account
          </NavLink>
          {user.role === "admin" && (
            <NavLink to="/app/admin" className={link} data-testid="nav-admin">
              <Shield className="w-4 h-4" /> Admin
            </NavLink>
          )}
        </nav>

        {/* Usage */}
        <div className="border-t border-zinc-800 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Plan</span>
            <span
              className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#FFD600] text-black"
              data-testid="plan-badge"
            >
              {user.plan}
            </span>
          </div>
          <div className="h-2 bg-zinc-800 mb-2">
            <div
              className="h-full bg-[#FFD600]"
              style={{ width: `${pct}%` }}
              data-testid="usage-bar"
            />
          </div>
          <div className="flex justify-between font-mono text-[10px] text-zinc-400">
            <span data-testid="usage-text">
              {user.generations_used}/{user.plan_limit} gens
            </span>
            <span>{pct}%</span>
          </div>
          <Link
            to="/pricing?checkout=starter"
            className="block mt-3 text-center bg-[#FFD600] text-black font-mono text-xs uppercase tracking-widest py-2 btn-hard"
            data-testid="upgrade-btn"
          >
            Unlock more
          </Link>
        </div>

        {/* User */}
        <div className="border-t border-zinc-800 p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm truncate" data-testid="user-name">{user.name}</div>
            <div className="font-mono text-[10px] text-zinc-500 truncate">{user.email}</div>
          </div>
          <button
            onClick={logout}
            className="p-2 text-zinc-400 hover:text-[#FF3333]"
            data-testid="logout-btn"
            title="Log out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto min-w-0">
        {announcements[0] && (
          <div className="border-b border-[#FFD600]/40 bg-[#FFD600] px-5 py-3 text-black" data-testid="active-announcement">
            <div className="mx-auto flex max-w-7xl flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-70">Live offer</div>
                <div className="font-heading text-2xl uppercase leading-tight">{announcements[0].title}</div>
                <div className="text-sm font-medium">{announcements[0].body}</div>
              </div>
              <Link
                to="/pricing?checkout=starter"
                className="btn-hard shrink-0 bg-black px-4 py-2 text-center font-mono text-xs uppercase tracking-widest text-white"
                data-testid="announcement-upgrade"
              >
                Upgrade
              </Link>
            </div>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
